import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';
import styles from './quiz-rating-field.module.scss';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBolt,
    faCircleDollar,
    faDollarSign,
    faFaceSmile,
    faGlass,
    faHeart,
    faMoon,
    faNotesMedical,
    faPerson,
    faPersonDress,
    faPeople,
    faPeopleSimple,
    faPills,
    faSackDollar,
    faStar,
    faSuitcaseMedical,
    faThumbsDown,
    faThumbsUp,
    faUser,
    faUtensils,
} from '@fortawesome/pro-regular-svg-icons';
import {
    faStar as faSolidStar,
    faFaceSmile as faSolidFaceSmile,
    faMoon as faSolidMoon,
    faBolt as faSolidBolt,
    faUtensils as faSolidUtensils,
    faGlass as faSolidGlass,
    faDollarSign as faSolidDollarSign,
    faSackDollar as faSolidSackDollar,
    faCircleDollar as faSolidCircleDollar,
    faHeart as faSolidHeart,
    faThumbsUp as faSolidThumbsUp,
    faThumbsDown as faSolidThumbsDown,
    faUser as faSolidUser,
    faPerson as faSolidPerson,
    faPersonDress as faSolidPersonDress,
    faPeople as faSolidPeople,
    faPeopleSimple as faSolidPeopleSimple,
    faPills as faSolidPills,
    faNotesMedical as faSolidNotesMedical,
    faSuitcaseMedical as faSolidSuitcaseMedical,
} from '@fortawesome/pro-solid-svg-icons';

const QUIZ_SELECT_SUBMIT_DELAY = 300;

const QUIZ_RATING_DEFAULT_ICON = faStar;
const QUIZ_RATING_DEFAULT_ICON_SOLID = faSolidStar;
const QUIZ_RATING_ICONS = {
    // Regular
    bolt: faBolt,
    'circle-dollar': faCircleDollar,
    'dollar-sign': faDollarSign,
    'face-smile': faFaceSmile,
    glass: faGlass,
    heart: faHeart,
    moon: faMoon,
    'notes-medical': faNotesMedical,
    people: faPeople,
    'people-simple': faPeopleSimple,
    person: faPerson,
    'person-dress': faPersonDress,
    pills: faPills,
    'sack-dollar': faSackDollar,
    'suitcase-medical': faSuitcaseMedical,
    'thumbs-down': faThumbsDown,
    'thumbs-up': faThumbsUp,
    user: faUser,
    utensils: faUtensils,
    star: faStar,
    // Solid
    'solid-bolt': faSolidBolt,
    'solid-circle-dollar': faSolidCircleDollar,
    'solid-dollar-sign': faSolidDollarSign,
    'solid-face-smile': faSolidFaceSmile,
    'solid-glass': faSolidGlass,
    'solid-heart': faSolidHeart,
    'solid-moon': faSolidMoon,
    'solid-notes-medical': faSolidNotesMedical,
    'solid-people': faSolidPeople,
    'solid-people-simple': faSolidPeopleSimple,
    'solid-person': faSolidPerson,
    'solid-person-dress': faSolidPersonDress,
    'solid-pills': faSolidPills,
    'solid-sack-dollar': faSolidSackDollar,
    'solid-suitcase-medical': faSolidSuitcaseMedical,
    'solid-thumbs-down': faSolidThumbsDown,
    'solid-thumbs-up': faSolidThumbsUp,
    'solid-user': faSolidUser,
    'solid-utensils': faSolidUtensils,
    'solid-star': faSolidStar,
};

const QuizRatingField = () => {
    const {
        currentQuestion,
        handleSubmitAnswer,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        state,
    } = useContext(QuizContext);
    const {
        questionId,
        questionText,
        questionExplanation,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        medication,
        additionalText,
    } = currentQuestion || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [, setContinueDisabled] = state.continueDisabled;
    const [, setContinueVisible] = state.continueVisible;
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const answerList = useMemo(
        () =>
            currentQuestion.answerList.map((o) => ({
                answerId: o.frontendStamp,
                answerText: o.answerText,
            })),
        [currentQuestion],
    );

    // Handle submitting
    const handleSubmit = useCallback(
        (i: number) => {
            const { answerId, answerText } = answerList[i];
            if (questionId && questionText) {
                handleSubmitAnswer({
                    questionId: questionId,
                    questionText: questionText,
                    answerIds: answerId ? [answerId] : undefined,
                    answerTexts: answerText ? [answerText] : undefined,
                    medication: medication,
                });
            }
        },
        [answerList, questionId, questionText, medication, handleSubmitAnswer],
    );
    const handleSelectAnswer = useCallback(
        (rating: number) => {
            // Set selected answer
            setSelectedIndex(rating);
            // Clear existing submit timeout if exists
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            // Set new submit timeout
            if (selectedIndex !== rating) {
                timeoutRef.current = setTimeout(() => {
                    handleSubmit(rating);
                }, QUIZ_SELECT_SUBMIT_DELAY);
            }
        },
        [selectedIndex, handleSubmit],
    );
    const handleMouseEnter = (rating: number) => setHoveredIndex(rating);
    const handleMouseLeave = () => setHoveredIndex(null);
    const getIsHovered = (rating: number) =>
        hoveredIndex && hoveredIndex >= rating;
    const getIsSelected = (rating: number) =>
        selectedIndex !== null && selectedIndex >= rating;
    // Set existing answer
    useEffect(() => {
        let selectedAnswerId = '';
        if (
            questionId &&
            submittedQuestionIds.includes(questionId) &&
            userAnswers.length > 0
        ) {
            selectedAnswerId = userAnswers[0].answerId;
        } else if (
            inQueue &&
            prefillNeeded &&
            prefillQuestionIds.includes(questionId) &&
            prefillAnswers.length > 0
        ) {
            selectedAnswerId = prefillAnswers[0].answerId;
        }
        if (selectedAnswerId) {
            setSelectedIndex(
                answerList.findIndex((a) => a.answerId === selectedAnswerId),
            );
        } else {
            setSelectedIndex(null);
        }
    }, [
        questionId,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        answerList,
    ]);
    // Continue click listener
    useEffect(() => {
        const submitCurrentSelectedAnswer = () => {
            if (selectedIndex) handleSubmit(selectedIndex);
        };
        window.addEventListener(
            QUIZ_EVENT_TYPES.continueClick,
            submitCurrentSelectedAnswer,
        );

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                submitCurrentSelectedAnswer,
            );
    }, [handleSubmit, selectedIndex]);
    // Enter / Number key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && selectedIndex) handleSubmit(selectedIndex);
            if (parseInt(e.key)) {
                const num = parseInt(e.key);
                if (num <= answerList.length) handleSelectAnswer(num - 1);
            }
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [answerList, handleSubmit, handleSelectAnswer, selectedIndex]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueDisabled(false);
            if (
                (userAnswers.length > 0 && selectedIndex !== null) ||
                (inQueue &&
                    prefillNeeded &&
                    prefillQuestionIds.includes(questionId) &&
                    prefillAnswers.length > 0)
            ) {
                setContinueVisible(true);
            } else {
                setContinueVisible(false);
            }
        }
    }, [
        questionId,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        prefillQuestionIds,
        pageTransitioning,
        inQueue,
        selectedIndex,
        setContinueDisabled,
        setContinueVisible,
    ]);

    return (
        <div className={classNames(styles['quiz-rating--wrapper'])}>
            {/* Content header */}
            <div className={classNames(styles['quiz-field-header'])}>
                {/* Title */}
                <h4 className={classNames(styles['title'])}>{questionText}</h4>
                {/* Subtitle */}
                {questionExplanation ? (
                    <h5
                        className={classNames(
                            styles['subtitle'],
                            'highlight pt-2',
                        )}
                    >
                        {questionExplanation}
                    </h5>
                ) : null}
            </div>
            {/* List of rating items */}
            <div
                className={classNames(styles['rating-list'])}
                onMouseLeave={handleMouseLeave}
            >
                {/* Item */}
                {answerList.map((a, i) => (
                    <button
                        data-testid="quiz-answer"
                        key={a.answerId}
                        className={classNames(
                            styles['rating-item'],
                            getIsHovered(i) ? styles['item-hovered'] : '',
                            getIsSelected(i) ? styles['item-selected'] : '',
                        )}
                        onClick={() => handleSelectAnswer(i)}
                        onMouseEnter={() => handleMouseEnter(i)}
                    >
                        {getIsHovered(i) || getIsSelected(i) ? (
                            <FontAwesomeIcon
                                icon={
                                    !!additionalText &&
                                    QUIZ_RATING_ICONS['solid-' + additionalText]
                                        ? QUIZ_RATING_ICONS[
                                              'solid-' + additionalText
                                          ]
                                        : QUIZ_RATING_DEFAULT_ICON_SOLID
                                }
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon={
                                    !!additionalText &&
                                    QUIZ_RATING_ICONS[additionalText]
                                        ? QUIZ_RATING_ICONS[additionalText]
                                        : QUIZ_RATING_DEFAULT_ICON
                                }
                            />
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default QuizRatingField;
